import { useState, useEffect, lazy, Suspense } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import IconButton from '@mui/material/IconButton';

import { 
    Close as CloseIcon, 
    Edit as EditIcon,
    Save as SaveIcon,     
    Delete as DeleteIcon,
    Share as ShareIcon,
    Print as PrintIcon,
    Undo as UndoDeleteIcon,
    RemoveRedEye as ViewIcon
} from '@mui/icons-material';

const Button = lazy(() => import('@/components/Button'));

const Input = lazy(() => import('@/components/Input'));
//import TextField from '@mui/material/TextField';

const DropdownTable = lazy(() => import('@/components/DropdownTable'));
const Switch = lazy(() => import('@/components/Switch'));
const InputRichText = lazy(() => import('@/components/InputRichText'));

import { html, newlines } from '@/utils/Format';
import { checkErrors } from "@/utils/Errors.jsx";
import { dbDateWithTimezoneToDbDate, date } from "@/utils/Dates";
import { getDefaultValue, checkDependencies, checkDependenciesDropdown } from '@/utils/Columns';
import { getModalStyle } from '@/utils/Modal';
import { getFileType, formatBytes, getFileInfoFromFilePath } from '@/utils/File';

// to show the info for a row from the grid
// to view, add or edit
export default function ItemModal({t, open, setOpen, router, fields, response, action, table, item, api, editable,
    enableAdd, enableEdit, modal,handleEdit, handleDelete, handleShare, getFirstEditableColumn,setRender,
    handleTableItemSelected,sendMessage,fullScreen,openUrlModal
}){

    const [ currentAction, setCurrentAction ] = useState(action ? action : 'view');

    // generic read only. if view mode or editable false, it is read only
    // if editable false but we force add/edit, not read only
    const [ readOnly, setReadOnly ] = useState((!editable && !enableAdd && !enableEdit) || !action || action === 'view' ? true : false);

    const [ data, setData ] = useState(null);

    // to check errors
    const [ errors, setErrors ] = useState(null);

    // to get focus on the first field if editing
    var isFocused = true;

    // to create the fields on data if empty
    const createDataFields = () => {

        var keys = Object.keys(fields);
            
        if(keys.length > 0){
        
            var newData = {};
            
            keys && keys?.map((key) => {

                var config = fields[key];

                // by default null value for every field, but check if default value coming from backend
                newData[key] = getDefaultValue(api,table,router,key,config);
            });

            setData(newData);
        }
    }

    // for some reason if you edit and add files and later you close, when reopening the uploaded files are still there
    // so remove them
    const cleanDataFiles = () => {

        var keys = Object.keys(fields);
            
        if(keys.length > 0){
            
            keys && keys?.map((key) => {

                var config = fields[key];

                if(config && config?.type === 'file' && item && item?.data && Object?.hasOwn(item.data,key)){

                    // if simple file with directly the link
                    // and initially not changed
                    if(config?.simple && !Array.isArray(item.data[key])){

                        if(item.data[key] && item.data[key]?.length > 0){

                            var path = item.data[key];
                            var {type,mime,name} = getFileInfoFromFilePath(t,path);
                            item.data[key] = [{
                                type: type,
                                mime: mime,
                                name: name,
                                path: path
                            }];
                        }

                    }
                    else{
                        var files = item.data[key];
                        item.data[key] = files?.filter(item => !item?.upload);
                    }
                }

            });
        }
    }

    // add the fields to data the first time
    useEffect(() => {

        if(fields){
            createDataFields();
        }

    }, [fields]);

    useEffect(() => {

        // if adding
        if(item === null){

            createDataFields();

            // check if dropdown and dependant on another dropdown because we need to change the possible options
            // possible options should be empty because the parent field is empty when creating unless it has a default value
            checkDependenciesDropdown(fields);

        }
        else{
            // if editing 

            // for some reason if you edit and add files and later you close, when reopening the uploaded files are still there
            // so remove them
            cleanDataFiles();            

            setData(item?.data);

            // check if dropdown and dependant on another dropdown because we need to change the possible options            
            checkDependenciesDropdown(fields,item?.data);
        }

        setReadOnly((!editable && !enableAdd && !enableEdit) || !currentAction || currentAction === 'view' ? true : false);

    }, [item,currentAction]);

    // detect 'action' change as well because if adding item, item will be null and useEffect won't execute
    useEffect(() => {
        
        if(open){

            setCurrentAction(action ? action : 'view');

            // if action is edit and item null, it means add. clean the form just in case there is previous info
            if(action && action === 'edit' && item === null){
                createDataFields();
            }
        }

    }, [open]);

    const { /*sxDialogTitle, sxCloseIcon,*/ sxDialogFullHeight, sxDialogContent } = getModalStyle();

    const handleCloseItemModal = () => {
        setOpen(false);
    }

    // when edition finished
    const handleFinishEdit = () => {        
        setOpen(false);
        /*
        // if adding, close modal
        if(!item){
            setOpen(false);
        }
        else{
            // if editing, change to view mode
            setCurrentAction('view');
        }
        */
    }

    const handleInput = (e) => {
        
        var name = e.target.name;
        var value = e.target.value;

        var newData = {...data};
        newData[name] = value;

        setData(newData);
    };

    const handleInputSwitch = (e) => {
        
        const name = e.target.name;
        var value = e.target.checked;        
        var newData = {...data};

        newData[name] = value;

        // check if there are dependant values        
        newData = checkDependencies(name,value,newData,fields);

        setData(newData);
    }
    
    const handleInputRichText = (name,value) => {
        var newData = {...data};
        newData[name] = value;
        setData(newData);
    }

    // to manage the dropdowns
    const handleDropdown = (type,field,dropdownData) => {

        var newData = {...data};

        if(type && type === 'multiple'){            
            newData[field] = dropdownData;
        }
        else{

            if(type && type === 'search'){
                newData[field] = dropdownData?.id ? dropdownData.id : null;
            }
            else{
                // single
                newData[field] = dropdownData;
                //newData[field] = dropdownData?.props?.value;
            }
        }

        // check if there are dependant values
        newData = checkDependencies(field,newData[field],newData,fields);
        setData(newData);        
    }

    // to manage files
    const handleFileInput = (e) => {

        const name = e.target.name;   
        //const file = e.target.files[0];
        const files = e.target.files;

        if(files && files?.length > 0){

            const newData = {...data};

            if(!Object.hasOwn(newData,name) || !newData[name]){
                newData[name] = [];                
            }

            Array.from(files).forEach((file) => {

                var mime = file?.type;
                var type = getFileType(mime);

                // create temporary url
                var objectUrl = URL.createObjectURL(file);
                
                // add item                    
                var newFile = {
                    type: type,
                    mime: mime,
                    name: file?.name ? file?.name : type && t('types.'+type) !== 'types.'+type ? t('types.'+type) : t('types.file'),
                    path: objectUrl,
                    upload: file
                };
                
                newData[name].push(newFile); 
                               
            });

            setData(newData);
        }
    }

    // to activate edition or save the item
    const handleEditItem = () => {

        if(!currentAction || currentAction === 'view'){

            if(modal){
                // enter edition mode
                setCurrentAction('edit');
            }
            else{
                // if not modal edition, close modal and open edition mode on the row

                // check if we are not already editing the same row 
                const cellsEditing = api.getEditingCells();

                const sameRow = cellsEditing.some(cellsEditing => {
                    return cellsEditing.rowIndex === item?.rowIndex;
                });

                if(!sameRow){

                    setTimeout(() => {

                        api?.startEditingCell({
                            rowIndex: item?.rowIndex,
                            colKey: getFirstEditableColumn(api)
                        });

                        item.setSelected(true); 

                        setOpen(false);

                    },0);
                }
                else{
                    setOpen(false);

                    // does not place cursor on it 
                    /*         
                    apiTable?.setFocusedCell({
                        rowIndex: node?.rowIndex,
                        colKey: getFirstEditableColumn(apiTable)
                    });
                    */
                }
            }
        }
        else{
            // create/edit => save info    

            // check errors on fields                   
            var errorsCheck = checkErrors(t,table,api,item,modal,fields,data,sendMessage,'save');

            // to show error on the fields helper texts
            if(errorsCheck && Object.keys(errorsCheck).length > 0){
                setErrors(errorsCheck);
            }
            else{
                setErrors(null);

                // if no errors, edit
                handleEdit('save',api,item,data,null,null,handleFinishEdit,setRender,handleTableItemSelected);
            }
        }
    }

    const handleDeleteItem = () => {
        const selectedRows = [item?.data];
        handleDelete(selectedRows);        
    }

    const handleShareItem = () => {        
        handleShare(item,'modal');        
    }

    const handlePrintItem = () => {
        window.print();
    }

    // custom titles
    const customTitles = {
        'add' : {
            'tickets' : t('tickets.actions.add')
        },
        'edit' : {
            //'tickets' : 'editarmando'
        }
    };

    // to remove files
    const handleRemoveFile = (e,field,index,restore = false) => {

        if(data[field][index] !== undefined && data[field][index] !== null){

            var newData = {...data};

            var deleteItem = 1;
            if(restore){
                deleteItem = 0;
            }

            newData[field][index].delete = deleteItem;            

            setData(newData);            
        }
    }

    return (
        
        <Dialog
            container={fullScreen?.active ? fullScreen?.node?.current : null}
            key={'table_'+table+'_item_'+item?.rowIndex+'_modal'}
            sx={sxDialogFullHeight}
            onClose={handleCloseItemModal}
            open={open}
            fullWidth={true}
            maxWidth='md'
            disableRestoreFocus
        >
            <DialogTitle className='modal-title'>
                {
                    Object.hasOwn(customTitles,!currentAction ? 'view' : currentAction === 'edit' && !item ? 'add' : currentAction) && 
                    Object.hasOwn(customTitles[!currentAction ? 'view' : currentAction === 'edit' && !item ? 'add' : currentAction],table) ?
                        customTitles[!currentAction ? 'view' : currentAction === 'edit' && !item ? 'add' : currentAction][table]
                    :
                        t('actions.'+currentAction+'.title')
                }
                {/*
                    ((!editable && !enableAdd && !enableEdit) || !currentAction || currentAction === 'view') ? 
                        Object.hasOwn(customTitles,currentAction) && Object.hasOwn(customTitles[currentAction],table) ?
                            customTitles[currentAction][table]
                        :
                            t('actions.'+currentAction+'.title')
                    :
                        !item ?
                            t('actions.add.title')//+'1'
                        :
                            t('actions.'+currentAction+'.title')
                */}
            </DialogTitle>
            {/*
            <IconButton                    
                onClick={handleCloseItemModal}
                sx={sxCloseIcon}
            >
                <CloseIcon />
            </IconButton> 
            */}
            <DialogContent className={sxDialogContent}>
                {
                    fields && Object.keys(fields)?.map((field) => {

                        // what to print if no value
                        const emptyValue = '-';

                        var config = fields[field];
                        var type = config?.type;                        
                        var render = null;

                        // check if the field has a config parameter editable
                        var fieldEditable = false;
                        
                        // if read only mode, not editable
                        // if id, not editable
                        // if editable config to true or it does not exist, field can be edited
                        if(
                            !readOnly &&
                            type !== 'id' &&
                            (
                                typeof config?.editable === 'undefined' || 
                                config?.editable === null || 
                                config?.editable === 1
                            )
                        ){
                            fieldEditable = true;
                        }

                        // if not editable and in edit mode (add or edit), hide the field
                        var hidden = false;
                        if(!fieldEditable && currentAction === 'edit'){
                            hidden = true;
                        }                        

                        // if not hidden
                        if(!hidden){

                            let names;
                            let fieldValue;
                            let values;
                            let value;
                            let valuesTranslated;

                            switch(type){

                                case 'actions':
                                    // if actions, do not show
                                    break
                                
                                case 'id':

                                    // id always not editable
                                    if(!fieldEditable){

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                {data && Object.hasOwn(data,field) ? data[field] : emptyValue}
                                            </Suspense>
                                        ;
                                    }
                                    else{
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <Input                                                 
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}
                                                    type={'text'}
                                                    value={data && Object.hasOwn(data,field) ? data[field] : null}
                                                    placeholder={t(table+'.fields.'+field)}
                                                    explanation={config?.explanation ? t(table+'.'+field+'.explanation') : null}
                                                    shrink
                                                    readOnly={true} // id always readOnly  
                                                    isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ; 

                                        // focus only once
                                        isFocused = false;
                                    }                               
                                    break;                                

                                case 'html':

                                    // if not editable
                                    if(!fieldEditable){

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                {
                                                    data && Object.hasOwn(data,field) && data[field] && data[field]?.length > 0 ?
                                                        <div dangerouslySetInnerHTML={{__html: html(data[field])}}></div>
                                                    :
                                                        ''
                                                }
                                            </Suspense>
                                        ;
                                    }
                                    else{

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <InputRichText                                                
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}                                                    
                                                    csrf_token={response?.data?.csrf_token}
                                                    value={data && Object.hasOwn(data,field) ? html(data[field]) : null}                      
                                                    placeholder={t(table+'.fields.'+field)}
                                                    onChange={(content) => handleInputRichText(field,content)}       
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}                                    
                                                />
                                            </Suspense>
                                            ;
                                    }
                                    break;

                                case 'boolean':
                                case 'boolean_badge':
                                case 'boolean_checkbox':

                                    fieldValue = false;
                                    if(data && Object.hasOwn(data,field) && data[field] !== null){
                                        fieldValue = data[field];
                                    }

                                    // if not editable                                    
                                    if(!fieldEditable){
                                                                            
                                        names = [t('types.boolean.no'),t('types.boolean.yes')];
                                        if(config && Object.hasOwn(config,'values')){                                                
                                            names = [t(table+'.'+field+'.'+config.values[0]),t(table+'.'+field+'.'+config.values[1])];
                                        }

                                        var fieldString = fieldValue ? names[1] : names[0];
                                
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {fieldString}
                                                </div>
                                            </Suspense>                                        
                                        ;
                                    }
                                    else{                              

                                        names = [t('types.boolean.no'),t('types.boolean.yes')];
                                        var multilabel = false;
                                        
                                        if(config && Object.hasOwn(config,'values')){                                                

                                            multilabel = true;
                                            names = [t(table+'.'+field+'.'+config.values[0]),t(table+'.'+field+'.'+config.values[1])];

                                            render = 
                                                <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                    <Switch                                                
                                                        id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                        name={field}                       
                                                        checked={fieldValue}
                                                        label={fieldEditable ? t(table+'.fields.'+field) : null}
                                                        multilabel={multilabel}
                                                        labeldisabled={names[0]}
                                                        labelenabled={names[1]}
                                                        onChange={fieldEditable ? handleInputSwitch : null}
                                                        fontSize='12px'                                           
                                                    />
                                                </Suspense>
                                            ;
                                        }   
                                        else{
                                            render = 
                                                <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                    <Switch      
                                                        //sxSwitch={{marginLeft:}}
                                                        id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                        name={field}                       
                                                        checked={fieldValue}
                                                        label={fieldEditable ? t(table+'.fields.'+field) : null}
                                                        labelPlacement={fieldEditable ? 'top' : ''}
                                                        onChange={fieldEditable ? handleInputSwitch : null}
                                                        fontSize='12px'                                                     
                                                    />
                                                </Suspense>
                                            ;
                                        }                                 
                                    }
                                    
                                    break;

                                case 'datetime':
                                case 'datetime_offset':
                                case 'date':
                                case 'hour':                                            

                                    // if not editable
                                    if(!fieldEditable){

                                        fieldValue = emptyValue;
                                        if(data && Object.hasOwn(data,field)){

                                            if(type === 'datetime' || type === 'datetime_offset'){
                                                fieldValue = date(data[field],true,true);
                                            }
                                            else{
                                                fieldValue = date(data[field]);
                                            }

                                            if(!fieldValue){
                                                fieldValue = emptyValue;
                                            }
                                        }

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {fieldValue}
                                                </div>
                                            </Suspense>
                                        ;
                                    }
                                    else{
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <Input                                                 
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}
                                                    type={hidden ? 'hidden' : type}
                                                    value={data && Object.hasOwn(data,field) ? type === 'datetime' || type === 'datetime_offset' ? dbDateWithTimezoneToDbDate(data[field]) : data[field] : null}
                                                    placeholder={t(table+'.fields.'+field)}
                                                    explanation={config?.explanation ? t(table+'.'+field+'.explanation') : null}
                                                    shrink
                                                    readOnly={!fieldEditable}
                                                    onChange={handleInput}
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                    isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ;

                                        // focus only once
                                        isFocused = false;
                                    }
                                    break;                                

                                case 'int':
                                case 'float':
                                case 'price':
                                case 'percentage':

                                    // if not editable
                                    if(!fieldEditable){
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {data && Object.hasOwn(data,field) ? data[field] ? data[field] : emptyValue : emptyValue}
                                                </div>
                                            </Suspense>
                                        ;
                                    }
                                    else{
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <Input                                               
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}
                                                    type={hidden ? 'hidden' : type === 'price' || type === 'percentage' ? 'float' : type}                                        
                                                    value={data && Object.hasOwn(data,field) ? data[field] : null}
                                                    placeholder={t(table+'.fields.'+field)}
                                                    explanation={config?.explanation ? t(table+'.'+field+'.explanation') : null}
                                                    shrink
                                                    readOnly={!fieldEditable}
                                                    onChange={handleInput}
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                    isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ;

                                        // focus only once
                                        isFocused = false;
                                    }
                                    break;

                                case 'dropdown':
                                case 'dropdown_search':

                                    values = config?.values;
                                    if(!values || values.length === 0){
                                        values = [];
                                    }

                                    // if not editable
                                    if(!fieldEditable){

                                        value = '';
                                        if(data && Object.hasOwn(data,field) && data[field] !== null){

                                            // if invalid option
                                            value = 'ID: '+data[field]+' ('+t('error.notvalid')+')';

                                            values.map((item) => {
                                                if(item?.id === data[field]){
                                                    
                                                    value = item?.name;

                                                    // if needs translation
                                                    if(config?.translate){
                                                        value = item?.name && item.name.length > 0 ? t(table+'.'+field+'.'+value) === table+'.'+field+'.'+value ? value : t(table+'.'+field+'.'+value) : '';
                                                    }

                                                    return;
                                                }
                                            });
                                        }

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {value ? value : emptyValue}
                                                </div>
                                            </Suspense>
                                        ;
                                    }
                                    else{

                                        // if needs translation
                                        if(config?.translate){
                                            valuesTranslated = [];
                                            values.map((item) => {
                                                valuesTranslated.push({
                                                    id:item?.id,
                                                    name: item?.name && item.name.length > 0 ? t(table+'.'+field+'.'+item.name) === table+'.'+field+'.'+item.name ? item.name : t(table+'.'+field+'.'+item.name) : ''
                                                });
                                            });   
                                            values = valuesTranslated;                                     
                                        }

                                        // forcing simple select to be autoComplete because with fullscreen it fails                                        
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <DropdownTable
                                                    t={t}                                                    
                                                    type={type && type === 'dropdown_search' ? 'search' : 'single'}
                                                    table={table}
                                                    column={field}
                                                    node={item}                                                    
                                                    value={data && Object.hasOwn(data,field) ? data[field] : null}
                                                    options={values}
                                                    shrink                                                    
                                                    handleDropdown={(data) => handleDropdown(type && type === 'dropdown_search' ? 'search' : 'single',field,data)}
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                    //isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ;

                                        // focus only once
                                        //isFocused = false;
                                    }
                                    break;

                                case 'dropdown_multiple':  

                                    values = config?.values;
                                    if(!values || values.length === 0){
                                        values = [];
                                    }

                                    // if not editable
                                    if(!fieldEditable){

                                        value = [];
                                        if(data && Object.hasOwn(data,field) && data[field] !== null && data[field].length > 0){

                                            var ids = [];
                                            data[field].map((item) => {
                                                ids.push(item?.id);
                                            });                                            

                                            var alreadyChecked = [];
                                            values.map((item) => {
                                                if(ids.includes(item?.id) && !alreadyChecked.includes(item?.id)){
                                                    
                                                    var itemName = item?.name;

                                                    // if needs translation
                                                    if(config?.translate){                                                    
                                                        itemName = itemName && itemName.length > 0 ? t(table+'.'+field+'.'+itemName) === table+'.'+field+'.'+itemName ? itemName : t(table+'.'+field+'.'+itemName) : '';
                                                    }

                                                    value.push(itemName);
                                                    
                                                    alreadyChecked.push(item.id);
                                                    
                                                    return;
                                                }
                                            });
                                        }

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {
                                                        value && value?.length > 0 ?
                                                            value.map((val,index) => (
                                                                <div key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_item_'+index}>
                                                                    {val}<br/>
                                                                </div>
                                                            ))
                                                        :
                                                            emptyValue
                                                    }
                                                </div>
                                            </Suspense>
                                        ;

                                        /*
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <Input                                                                      
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}                                                
                                                    type={hidden ? 'hidden' : 'multiline'}
                                                    value={value.join('\n')}
                                                    placeholder={t(table+'.fields.'+field)}
                                                    shrink
                                                    readOnly={true}
                                                    onChange={handleInput}                                                                                                
                                                    sx={{padding:0,margin:0,border:'none'}}
                                                />
                                            </Suspense>
                                        ;
                                        */

                                        /*
                                        render = 
                                            <TextField
                                                id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field} 
                                                name={field}
                                                label={t(table+'.fields.'+field)}                                            
                                                readOnly={readOnly}
                                                fullWidth
                                                size='small'                                                
                                                InputProps={{
                                                    //...InputProps,
                                                    //...inputProperties,
                                                    startAdornment: (
                                                        value?.map((name,index) => (                                                            
                                                            <span key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_index_'+index}>
                                                                <Chip                                            
                                                                    sx={sxChip}
                                                                    label={name}
                                                                    variant="outlined"
                                                                    onClick={()=>{}}
                                                                />                               
                                                            </span>
                                                        ))
                                                    )
                                                }}                                                
                                            />;
                                        */
                                    }
                                    else{     

                                        // if needs translation
                                        if(config?.translate){
                                            valuesTranslated = [];
                                            values.map((item) => {
                                                valuesTranslated.push({
                                                    id:item?.id,
                                                    name: item?.name && item.name.length > 0 ? t(table+'.'+field+'.'+item?.name) === table+'.'+field+'.'+item?.name ? item?.name : t(table+'.'+field+'.'+item?.name) : ''
                                                });
                                            });   
                                            values = valuesTranslated;                                     
                                        }

                                        // edit
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <DropdownTable
                                                    t={t}
                                                    type='multiple'
                                                    table={table}
                                                    column={field}
                                                    node={item} 
                                                    value={data && Object.hasOwn(data,field) ? data[field] : null}
                                                    options={values}
                                                    shrink
                                                    handleDropdown={(data) => handleDropdown('multiple',field,data)}
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                    //isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ;

                                        // focus only once
                                        //isFocused = false;
                                    }
                                    
                                    break;

                                case 'file':

                                    render = null;

                                    // if editable add form input to add files
                                    if(fieldEditable){

                                        var isMultiple = false; 
                                        var multiplePlaceholder = '';

                                        // if type file but simple, it means link directly on the field)
                                        if(!config?.simple){

                                            // if no max property, no limit                                            
                                            if(!Object.hasOwn(config,'max')){
                                                isMultiple = true;
                                            }
                                            else{
                                                if(config?.max && config.max > 1){
                                                     isMultiple = true;
                                                     multiplePlaceholder = ' (max: '+config.max+')';
                                                }
                                            }
                                        }

                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className='mb-4'>
                                                    <Input                                               
                                                        id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}                                                    
                                                        name={field}
                                                        type='file'                                        
                                                        value={data && Object.hasOwn(data,field) ? data[field] : null}
                                                        placeholder={t(table+'.fields.'+field)+multiplePlaceholder}                                                    
                                                        shrink                                                    
                                                        onChange={(e) => handleFileInput(e)}
                                                        error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                        accept={config?.accept && config.accept?.length > 0 ? config.accept : null}
                                                        multiple={isMultiple}
                                                    />
                                                </div>
                                            </Suspense>
                                        ;
                                    }

                                    // if editable or not editable
                                    // show links and option to remove them                                    
                                    if(data && Object.hasOwn(data,field) && data[field]){
                                        
                                        var files = data[field];
                                        
                                        if(files?.length > 0){

                                            render = 
                                                <>
                                                {render}
                                                <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                    <div className={'item-modal-link-files'}>
                                                        {
                                                            files?.map((item,index) => {

                                                                var linkText = item?.name ? item.name : item?.type && t('types.'+item.type) !== 'types.'+item.type ? t('types.'+item.type) : t('types.file');                                                                
                                                                
                                                                return (                                                                        
                                                                    <div 
                                                                        key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_files_'+index}
                                                                        className={`
                                                                            ${
                                                                                item.type === 'image' ? 
                                                                                    'item-modal-link-file-preview-div' 
                                                                                : 
                                                                                    'item-modal-link-file-div'} 
                                                                            ${
                                                                                item?.upload && errors && Object.hasOwn(errors,field)? 
                                                                                    'border-red-500' 
                                                                                : 
                                                                                    ''
                                                                            }
                                                                    `}
                                                                    >
                                                                        {
                                                                            fieldEditable &&
                                                                            <div className='mb-2 text-center'>
                                                                                <Button 
                                                                                    className='app-button'
                                                                                    onClick={(e) => handleRemoveFile(e,field,index,data[field][index]?.delete ? true : false)}
                                                                                    text={data[field][index]?.delete ? t('actions.restore.button') : t('actions.delete.button')}
                                                                                    type={data[field][index]?.delete ? 'restore' : 'delete'}
                                                                                    variant={data[field][index]?.delete ? '' : 'contained'}
                                                                                />
                                                                            </div>
                                                                        }
                                                                        <div className='truncate mb-2'>{linkText}</div>
                                                                        {
                                                                            fieldEditable ?
                                                                                item?.upload ? 
                                                                                    <div className='font-bold text-red-600 text-sm truncate'>{t('file.new')}</div> 
                                                                                : 
                                                                                    <div className='text-sm truncate'>{t('file.existent')}</div> 
                                                                            :
                                                                                ''
                                                                        }          
                                                                        <div className='text-xs mb-1 truncate'>
                                                                            {
                                                                                fieldEditable ?

                                                                                    item?.upload && item.upload?.size ? 
                                                                                        formatBytes(item.upload.size) 
                                                                                    : 
                                                                                        '-'
                                                                                :
                                                                                    ''
                                                                            }
                                                                        </div>
                                                                        <div className={`item-modal-file-div ${fieldEditable && data[field][index]?.delete && 'item-modal-link-file-div-deleted'}`}>
                                                                            {
                                                                                (!config?.target || config.target === 'modal') && openUrlModal ?
                                                                                    <a 
                                                                                        key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_file_'+index} 
                                                                                        className={`${item.type === 'image' ? 'item-modal-link-image cursor-pointer' : 'item-modal-link-file cursor-pointer'}`} 
                                                                                        onClick={() => openUrlModal(item?.path,true,item?.type,item?.mime,item?.name)}
                                                                                    >                                                                                        
                                                                                        {
                                                                                            item.type === 'image' ? 
                                                                                                <div className='item-modal-image-div'>
                                                                                                    <img src={item?.path}/>
                                                                                                </div>
                                                                                            :
                                                                                                item.type === 'pdf' ?
                                                                                                    <div className='item-modal-file'>
                                                                                                        <iframe
                                                                                                            src={item?.path}                    
                                                                                                            style={{width:'100%', height: '100%', pointerEvents: 'none'}}
                                                                                                        ></iframe>
                                                                                                    </div>
                                                                                                :
                                                                                                    <div className='item-modal-file'><ViewIcon fontSize='large'/></div>
                                                                                        }                                                                                        
                                                                                    </a>
                                                                                :
                                                                                    <a 
                                                                                        key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_file_'+index} 
                                                                                        className={`${item.type === 'image' ? 'item-modal-link-image' : 'item-modal-link-file'}`} 
                                                                                        href={item?.path} 
                                                                                        target={config?.target}
                                                                                    >                                                                                
                                                                                        {
                                                                                            item.type === 'image' ?
                                                                                                <div className='item-modal-image-div'>
                                                                                                    <img src={item?.path}/>
                                                                                                </div>
                                                                                            :                                                                                            
                                                                                                item.type === 'pdf' ?
                                                                                                    <div className='item-modal-file'>
                                                                                                        <iframe
                                                                                                            src={item?.path}                    
                                                                                                            style={{width:'100%', height: '100%', pointerEvents: 'none'}}
                                                                                                        ></iframe>
                                                                                                    </div>
                                                                                                :
                                                                                                    <div className='item-modal-file'><ViewIcon fontSize='large'/></div>
                                                                                        }
                                                                                        
                                                                                    </a>
                                                                            }
                                                                        </div>                                                                        
                                                                    </div> 
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </Suspense>
                                                </>
                                            ;
                                        }
                                    }
                                    else{
                                        <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                            <div className=''>
                                                {emptyValue}
                                            </div>
                                        </Suspense>
                                    }                                    

                                    break;
                                
                                default:

                                    // text, textlong

                                    // if not editable
                                    if(!fieldEditable){
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <div className=''>
                                                    {
                                                        data && Object.hasOwn(data,field) && data[field] && data[field]?.length > 0 ?
                                                            
                                                            (type === 'link' || type === 'linkshort' || type === 'email' || type === 'phone') ?

                                                                (!config?.target || config.target === 'modal') && openUrlModal ?
                                                                    <a className='item-modal-link cursor-pointer' onClick={() => openUrlModal(data[field])}>
                                                                        {
                                                                            type === 'linkshort' ?
                                                                                t('types.link')
                                                                            :
                                                                                type === 'link' && config && Object.hasOwn(config,'value') ?
                                                                                    t(table+'.'+field+'.'+config.value)
                                                                                :
                                                                                    data[field]
                                                                        }
                                                                    </a>
                                                                :
                                                                    <a 
                                                                        className='item-modal-link' 
                                                                        href={`${type === 'email' ? 'mailto:' : type === 'phone' ? 'tel:' : '' }${data[field]}`} 
                                                                        target={type === 'email' || type === 'phone' ? '_blank' : config?.target }>
                                                                        {
                                                                            type === 'linkshort' ?
                                                                                t('types.link')
                                                                            :
                                                                                type === 'link' && config && Object.hasOwn(config,'value') ?
                                                                                    t(table+'.'+field+'.'+config.value)
                                                                                :
                                                                                    data[field]
                                                                        }
                                                                    </a>
                                                            :
                                                                type === 'textlong' ?
                                                                    <div dangerouslySetInnerHTML={{__html: newlines(data[field])}}></div>
                                                                :
                                                                    config?.values && config && Object.hasOwn(config.values,data[field]) ? 
                                                                        t(table+'.'+field+'.'+config.values[data[field]]) 
                                                                    : 
                                                                        data[field]
                                                        :
                                                            emptyValue
                                                    }
                                                </div>
                                            </Suspense>
                                        ;
                                    }
                                    else{
                                        render = 
                                            <Suspense key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}>
                                                <Input                                               
                                                    id={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field}
                                                    name={field}
                                                    type={type === 'textlong' ? 'multiline' : 'text'}
                                                    value={data && Object.hasOwn(data,field) ? data[field] : null}                      
                                                    placeholder={t(table+'.fields.'+field)}
                                                    explanation={config?.explanation ? t(table+'.'+field+'.explanation') : null}
                                                    shrink                                                    
                                                    onChange={handleInput}                                        
                                                    rows={type === 'textlong' ? 4 : null}
                                                    error={errors && Object.hasOwn(errors,field) ? errors[field] : ''}
                                                    isFocused={isFocused}
                                                />
                                            </Suspense>
                                        ;

                                        // focus only once
                                        isFocused = false;
                                    }
                                    break;
                            }                        

                            // if read only, add title above the value
                            /*if(readOnly){
                                var title = <div>fds</div>;
                                render = title+render;
                            }*/                            

                            return (
                                render &&
                                <div 
                                    key={'table_'+table+'_item_'+item?.rowIndex+'_field_'+field+'_div'}
                                    className='mb-6'
                                >
                                    {
                                        !fieldEditable &&
                                        <div className='item-modal-title'>
                                            {t(table+'.fields.'+field)}
                                        </div>
                                    }
                                    {render}
                                </div>
                            );
                        }
                    })
                }
            </DialogContent>
            <div className="modal-buttons">
                {
                    (editable || enableAdd || enableEdit) /*&& modal*/ &&   
                    <>             
                    {
                        (!currentAction || currentAction === 'view' || currentAction === 'edit') &&
                        <div className='mr-1'>
                            <Suspense>
                                {
                                    (!currentAction || currentAction === 'view') ? 
                                        <EditIcon className='action-button-modal' onClick={handleEditItem}/>
                                    :
                                        <SaveIcon className='action-button-modal text-green-800' onClick={handleEditItem}/>
                                }
                                {/*
                                <Button        
                                    className='mx-1'       
                                    onClick={handleEditItem}                                     
                                    type={!currentAction || currentAction === 'view'  ? 'edit' : !item ? 'add' : 'save'}
                                    text={!currentAction || currentAction === 'view'  ? t('actions.edit.button') : !item ? t('actions.add.button') : t('actions.save.button')}
                                />
                                */}                                
                            </Suspense>
                        </div>
                    }
                    {
                        editable && item &&
                        <div className='mr-1'>
                            <Suspense>
                                <DeleteIcon className='action-button-modal text-red-500' onClick={handleDeleteItem}/>
                                {/*
                                <Button     
                                    onClick={handleDeleteItem}
                                    type='delete'
                                    text={t('actions.delete.button')}
                                />
                                */}
                            </Suspense>
                        </div>
                    }
                    {
                        (!currentAction || currentAction === 'view') &&
                        <>
                        <div className='mr-1'>
                            <PrintIcon className='action-button-modal text-gray-500' onClick={handlePrintItem}/>
                        </div>
                        <div className='mr-1'>
                            <ShareIcon className='action-button-modal text-blue-500' onClick={handleShareItem}/>
                        </div>
                        </>
                    }   
                    </>                 
                }
                <div className=''>
                    <CloseIcon className='action-button-modal text-black' onClick={handleCloseItemModal}/>
                </div>
            </div>
        </Dialog>
  );
}